// src/components/BookCover/PdfPreviewModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { buildJsPdf } from './usePdfGenerator';
import '../../styles/PdfPreviewModal.css';

const FACE_BADGE_COLORS = {
  'Front Cover': '#8b5cf6',
  'Back Cover':  '#0ea5e9',
  'Spine':       '#f59e0b',
};

const FACE_LABELS = {
  front: 'Front Cover',
  back:  'Back Cover',
  spine: 'Spine',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function makeSet(pages, predicate) {
  return new Set(pages.reduce((acc, p, i) => { if (predicate(p)) acc.push(i); return acc; }, []));
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function defaultCheckedSet(pages) {
  // Check everything except isolated face pages (opt-in)
  return makeSet(pages || [], p => p.type !== 'face');
}

const PRESETS = [
  { label: 'All',          fn: pages => makeSet(pages, () => true) },
  { label: 'None',         fn: ()    => new Set() },
  { label: 'Cover+Table',  fn: pages => makeSet(pages, p => p.type === 'cover' || p.type === 'table') },
  { label: 'Cover',        fn: pages => makeSet(pages, p => p.type === 'cover') },
  { label: 'Front',        fn: pages => makeSet(pages, p => p.type === 'face' && p.face === 'front') },
  { label: 'Back',         fn: pages => makeSet(pages, p => p.type === 'face' && p.face === 'back') },
  { label: 'Spine',        fn: pages => makeSet(pages, p => p.type === 'face' && p.face === 'spine') },
  { label: 'Elements',     fn: pages => makeSet(pages, p => p.type === 'element') },
];

// ── Page card renderers ──────────────────────────────────────────────────────

function CoverCard({ page }) {
  const leftFoldPct  = (page.faceW / page.totalW) * 100;
  const rightFoldPct = ((page.faceW + page.spineW) / page.totalW) * 100;

  return (
    <div className="ppm-sheet ppm-sheet--landscape">
      <div className="ppm-cover-header">
        <span className="ppm-cover-title">BOOK COVER — PRINT SPECIFICATION</span>
      </div>
      <div className="ppm-cover-image-wrap">
        <div
          className="ppm-cover-img-container"
          style={{ aspectRatio: `${page.totalW} / ${page.faceH}` }}
        >
          <img className="ppm-cover-img" src={page.imageUrl} alt="Full wraparound cover" />
          <div className="ppm-fold-line" style={{ left: `${leftFoldPct}%` }} />
          <div className="ppm-fold-line" style={{ left: `${rightFoldPct}%` }} />
        </div>
      </div>
    </div>
  );
}

function ElementCard({ page }) {
  const badgeColor = FACE_BADGE_COLORS[page.faceLabel] || '#888';
  return (
    <div className="ppm-sheet ppm-sheet--portrait">
      <div className="ppm-elem-header">
        <span className="ppm-elem-header-label">{page.elementLabel}</span>
        <span className="ppm-elem-face-badge" style={{ background: badgeColor }}>{page.faceLabel}</span>
      </div>
      <div className="ppm-elem-body">
        <div className="ppm-elem-spotlight-col">
          {page.spotlightUrl ? (
            <img className="ppm-elem-spotlight-img" src={page.spotlightUrl} alt={`${page.faceLabel} — ${page.elementLabel}`} />
          ) : (
            <div className="ppm-elem-spotlight-placeholder">No preview</div>
          )}
          <span className="ppm-elem-face-sublabel">{page.faceLabel}</span>
        </div>
        <div className="ppm-elem-divider" />
        <div className="ppm-elem-specs-col">
          <p className="ppm-specs-section-label">PRINT SPECIFICATIONS</p>
          <hr className="ppm-specs-rule" />
          <div className="ppm-specs-field">
            <span className="ppm-specs-field-key">Element</span>
            <span className="ppm-specs-field-val">{page.elementLabel}</span>
          </div>
          <div className="ppm-specs-field">
            <span className="ppm-specs-field-key">Face</span>
            <span className="ppm-specs-field-val">{page.faceLabel}</span>
          </div>
          <div className="ppm-specs-field">
            <span className="ppm-specs-field-key">Ref ID</span>
            <span className="ppm-specs-field-val ppm-specs-id">{page.elementId}</span>
          </div>
          <hr className="ppm-specs-rule ppm-specs-rule--mt" />
          <p className="ppm-specs-section-label">INSTRUCTIONS</p>
          {(!page.comments || page.comments.length === 0) ? (
            <p className="ppm-specs-empty">No specifications set.</p>
          ) : (
            <div className="ppm-specs-comments">
              {page.comments.map((c, i) => (
                <div key={i} className="ppm-specs-comment">
                  <span className="ppm-specs-comment-author">{c.userName || 'Note'}</span>
                  <p className="ppm-specs-comment-text">{c.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FaceCard({ page }) {
  const label = FACE_LABELS[page.face] || page.face;
  const badgeColor = FACE_BADGE_COLORS[label] || '#888';
  return (
    <div className="ppm-sheet ppm-sheet--portrait">
      <div className="ppm-elem-header">
        <span className="ppm-elem-header-label">{label} — Isolated</span>
        <span className="ppm-elem-face-badge" style={{ background: badgeColor }}>{label}</span>
      </div>
      <div className="ppm-face-image-wrap">
        <img className="ppm-face-img" src={page.imageUrl} alt={label} />
      </div>
    </div>
  );
}

function TableCard({ page }) {
  const hasRows = page.rows && page.rows.length > 0;
  return (
    <div className="ppm-sheet ppm-sheet--portrait">
      <div className="ppm-table-header"><span>PRINT SPECIFICATIONS SUMMARY</span></div>
      <div className="ppm-table-body">
        <table className="ppm-summary-table">
          <thead>
            <tr>
              <th>#</th><th>Element</th><th>Face</th><th>Ref ID</th><th>Specifications</th>
            </tr>
          </thead>
          <tbody>
            {!hasRows ? (
              <tr><td>—</td><td>All elements</td><td>—</td><td>—</td><td>Normal — no special specifications</td></tr>
            ) : (
              <>
                {page.rows.map((row, i) => (
                  <tr key={i} className={i % 2 === 1 ? 'ppm-row-alt' : ''}>
                    <td>{i + 1}</td>
                    <td>{row.label}</td>
                    <td>
                      <span className="ppm-face-chip" style={{ background: FACE_BADGE_COLORS[row.faceLabel] || '#555' }}>
                        {row.faceLabel}
                      </span>
                    </td>
                    <td className="ppm-id-cell">{row.elementId}</td>
                    <td className="ppm-instructions-cell">{row.instructions}</td>
                  </tr>
                ))}
                <tr className={page.rows.length % 2 === 1 ? 'ppm-row-alt' : ''}>
                  <td>—</td><td>All other elements</td><td>—</td><td>—</td><td>Normal</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Thumbnail ────────────────────────────────────────────────────────────────

function Thumbnail({ page, index, isActive, isChecked, onSelect, onToggle }) {
  const faceLabel = page.type === 'face' ? FACE_LABELS[page.face] : page.faceLabel;
  const badgeColor = (page.type === 'element' || page.type === 'face')
    ? (FACE_BADGE_COLORS[faceLabel] || '#888')
    : null;

  const thumbLabel =
    page.type === 'cover'   ? 'Cover' :
    page.type === 'table'   ? 'Summary' :
    page.type === 'face'    ? `${FACE_LABELS[page.face] || page.face} isolated` :
    page.elementLabel;

  return (
    <div
      className={`ppm-thumb${isActive ? ' ppm-thumb--active' : ''}${isChecked ? ' ppm-thumb--checked' : ''}`}
      onClick={() => onSelect(index)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onSelect(index)}
      title={thumbLabel}
    >
      {/* Top row: page number + checkbox toggle */}
      <div className="ppm-thumb-toprow">
        <span className="ppm-thumb-num">{index + 1}</span>
        <span
          className={`ppm-thumb-check${isChecked ? ' ppm-thumb-check--on' : ''}`}
          onClick={e => { e.stopPropagation(); onToggle(index); }}
          title={isChecked ? 'Remove from download' : 'Include in download'}
        >
          {isChecked ? '✓' : ''}
        </span>
      </div>

      {/* Image preview */}
      {page.type === 'cover' && (
        <div className="ppm-thumb-img-wrap">
          <img src={page.imageUrl} alt="cover" className="ppm-thumb-img" />
        </div>
      )}
      {page.type === 'element' && (
        <div className="ppm-thumb-img-wrap">
          {page.spotlightUrl
            ? <img src={page.spotlightUrl} alt="element" className="ppm-thumb-img" />
            : <div className="ppm-thumb-placeholder" />
          }
        </div>
      )}
      {page.type === 'face' && (
        <div className="ppm-thumb-img-wrap">
          <img src={page.imageUrl} alt={page.face} className="ppm-thumb-img" />
        </div>
      )}
      {page.type === 'table' && (
        <div className="ppm-thumb-table-icon">☰</div>
      )}

      <span className="ppm-thumb-label">
        {thumbLabel.length > 18 ? thumbLabel.slice(0, 18) + '…' : thumbLabel}
        {badgeColor && <span className="ppm-thumb-face-dot" style={{ background: badgeColor }} />}
      </span>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function PdfPreviewModal({ pages, pdfGenerating, versionName, onClose }) {
  const [activeIdx, setActiveIdx]     = useState(0);
  const [checkedSet, setCheckedSet]   = useState(() => defaultCheckedSet(pages));
  const [downloading, setDownloading] = useState(false);
  const mainRef = useRef(null);

  // Reset state when pages change (new generation)
  useEffect(() => {
    setActiveIdx(0);
    setCheckedSet(defaultCheckedSet(pages));
  }, [pages]);

  const activePage = pages?.[activeIdx];
  const checkedCount = checkedSet.size;

  const activePresetIdx = pages?.length
    ? PRESETS.findIndex(p => setsEqual(p.fn(pages), checkedSet))
    : -1;

  const applyPreset = (preset) => {
    setCheckedSet(preset.fn(pages || []));
  };

  const goTo = (idx) => {
    setActiveIdx(idx);
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleCheck = (idx) => {
    setCheckedSet(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleDownload = () => {
    const selected = (pages || []).filter((_, i) => checkedSet.has(i));
    if (!selected.length) return;
    setDownloading(true);
    try {
      const pdf = buildJsPdf(selected, versionName || '');
      if (pdf) {
        const date = new Date().toISOString().slice(0, 10);
        pdf.save(`book-cover-specs-${date}.pdf`);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goTo(Math.min(activeIdx + 1, (pages?.length || 1) - 1));
    if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   goTo(Math.max(activeIdx - 1, 0));
  };

  return (
    <div
      className="ppm-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="ppm-modal">
        {/* ── Top bar ── */}
        <div className="ppm-topbar">
          <span className="ppm-topbar-title">
            Print Specification Preview
            {pages?.length > 0 && (
              <span className="ppm-topbar-count"> · {pages.length} page{pages.length !== 1 ? 's' : ''}</span>
            )}
          </span>

          <div className="ppm-topbar-actions">
            {pdfGenerating && <span className="ppm-generating-label">⏳ Generating…</span>}
            <button
              className="ppm-download-btn"
              onClick={handleDownload}
              disabled={pdfGenerating || downloading || !pages?.length || checkedCount === 0}
              title={checkedCount === 0 ? 'Select pages in sidebar to download' : `Download ${checkedCount} page${checkedCount !== 1 ? 's' : ''}`}
            >
              {downloading ? 'Building…' : `⬇ Download${checkedCount > 0 ? ` (${checkedCount})` : ''}`}
            </button>
            <button className="ppm-close-btn" onClick={onClose} title="Close">✕</button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="ppm-body">
          {/* Sidebar */}
          <div className="ppm-sidebar">
            {/* Preset pills */}
            {pages?.length > 0 && (
              <div className="ppm-presets">
                {PRESETS.map((preset, pi) => (
                  <button
                    key={preset.label}
                    className={`ppm-preset${activePresetIdx === pi ? ' ppm-preset--active' : ''}`}
                    onClick={() => applyPreset(preset)}
                    title={preset.label}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}

            {/* Thumbnails */}
            <div className="ppm-sidebar-thumbs">
              {pdfGenerating && !pages?.length ? (
                <div className="ppm-sidebar-loading">
                  <div className="ppm-spinner" />
                  <span>Building preview…</span>
                </div>
              ) : (
                pages?.map((page, i) => (
                  <Thumbnail
                    key={i}
                    page={page}
                    index={i}
                    isActive={i === activeIdx}
                    isChecked={checkedSet.has(i)}
                    onSelect={goTo}
                    onToggle={toggleCheck}
                  />
                ))
              )}
            </div>
          </div>

          {/* Main area */}
          <div className="ppm-main" ref={mainRef}>
            {pdfGenerating && !pages?.length ? (
              <div className="ppm-main-loading">
                <div className="ppm-spinner ppm-spinner--large" />
                <p className="ppm-main-loading-text">Generating preview…</p>
                <p className="ppm-main-loading-sub">Fetching annotations and compositing element highlights.</p>
              </div>
            ) : !activePage ? (
              <div className="ppm-main-loading">
                <p className="ppm-main-loading-text">No pages generated.</p>
              </div>
            ) : (
              <>
                {activePage.type === 'cover'   && <CoverCard   page={activePage} />}
                {activePage.type === 'element' && <ElementCard page={activePage} />}
                {activePage.type === 'face'    && <FaceCard    page={activePage} />}
                {activePage.type === 'table'   && <TableCard   page={activePage} />}

                {pages.length > 1 && (
                  <div className="ppm-page-nav">
                    <button className="ppm-nav-btn" onClick={() => goTo(Math.max(activeIdx - 1, 0))} disabled={activeIdx === 0}>
                      ‹ Prev
                    </button>
                    <span className="ppm-page-indicator">Page {activeIdx + 1} of {pages.length}</span>
                    <button className="ppm-nav-btn" onClick={() => goTo(Math.min(activeIdx + 1, pages.length - 1))} disabled={activeIdx === pages.length - 1}>
                      Next ›
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
